import React, { useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useBooksByTag } from '@/features/tags/hooks/useBooksByTag';
import type { TagBookItem } from '@/features/tags/api/tags';
import BookCard from '@/features/library/components/BookCard';

const TagBooksPage: React.FC = () => {
  const { tagId } = useParams<{ tagId: string }>();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const tagIdNum = tagId ? parseInt(tagId, 10) : 0;
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const { 
    books, 
    isLoading, 
    isFetchingNextPage, 
    hasNextPage, 
    fetchNextPage, 
    isError,
    tagName: apiTagName 
  } = useBooksByTag(tagIdNum || null, { enabled: !!tagIdNum, limit: 10 });

  const tagName: string = location?.state?.tagName || apiTagName || '';

  // Infinite scroll observer
  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [target] = entries;
    if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      root: null,
      rootMargin: '100px',
      threshold: 0.1,
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  // Shimmer loading component
  const ShimmerGrid = ({ count = 6 }: { count?: number }) => (
   <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="flex flex-col">
          <div className="w-full aspect-[2/3] bg-gray-200 rounded-xl animate-pulse mb-2"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse mb-1 w-full"></div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
        </div>
      ))}
    </div>
  );

  return (
     <div className="min-h-screen" style={{ backgroundColor: '#f5f7fa' }} dir="rtl">
      {/* Header */}
       <div className="sticky top-0 z-50 flex items-center justify-between px-4 py-3 mb-4 bg-[#f5f7fa] shadow-sm pt-safe-top">
        <button
          onClick={() => navigate(-1)}
          className="p-3 bg-white rounded-xl hover:bg-gray-50 transition-colors shadow-sm border border-gray-200"
        >
          <svg className="w-6 h-6 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"></path>
          </svg>
        </button>
        <div className="text-center flex-1 mt-2">
          <h1 className="text-lg font-bold text-foreground font-persian">
            {tagName ? `کتاب های مرتبط با ${tagName}` : 'کتاب های مرتبط'}
          </h1>
        </div>
        <div className="w-12" />
      </div>

       <div className="px-4 pb-20">
        {/* Initial loading */}
        {isLoading && <ShimmerGrid count={8} />}

        {/* Error state */}
        {isError && (
          <div className="text-center py-8 bg-red-50 rounded-xl border border-red-100">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <p className="text-red-600 text-sm font-persian font-medium">خطا در بارگذاری کتاب‌ها</p>
            <p className="text-red-500 text-xs font-persian">لطفاً دوباره تلاش کنید</p>
          </div>
        )}

        {/* Books grid */}
        {!isLoading && !isError && (
          books && books.length > 0 ? (
            <>
               <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-x-3 gap-y-6">
                {books.map((b: TagBookItem) => (
                  <BookCard
                    key={b.book_id}
                    book={{
                      book_id: b.book_id,
                      title: b.title,
                      author: b.author,
                      cover_image_url: b.cover_image_url,
                      average_rating: b.average_rating,
                    }}
                  />
                ))}
              </div>
              
              {/* Load more trigger */}
              <div ref={loadMoreRef} className="py-4">
                {isFetchingNextPage && (
                  <div className="flex justify-center">
                     <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-x-3 gap-y-6 w-full">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="flex flex-col">
                          <div className="w-full aspect-[2/3] bg-gray-200 rounded-xl animate-pulse mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded animate-pulse mb-1"></div>
                          <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              </div>
              <p className="text-gray-500 text-sm font-persian font-medium">کتابی برای این موضوع یافت نشد</p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default TagBooksPage;
